"use client";

import { useEffect } from "react";
import { requestPersistentStorage } from "@/lib/storage-persistence";

/**
 * Registers the service worker (public/sw.js). Mounted once from the
 * root layout (src/app/layout.tsx); renders nothing.
 *
 * Production-only ON PURPOSE: in dev the service worker would fight
 * HMR (Turbopack rewrites /_next/static/ chunks in place, but the
 * worker's cache-first branch would keep serving the first copy it
 * saw) and pollute the browser cache with dev-build assets that then
 * shadow real production assets on the same origin. NODE_ENV is
 * inlined at build time, so the entire branch is dead code in dev
 * bundles.
 */
export function SwRegister() {
  useEffect(() => {
    // Ask for persistent storage BEFORE the production gate below.
    //
    // Not a service-worker concern, but the same "runs once on load, renders
    // nothing" slot, and it must not be production-only: the offline write
    // queue is testable in dev and its durability should be too.
    //
    // Everything this app keeps offline is in IndexedDB, which is best-effort
    // by default - evictable under pressure, and cleared by iOS for origins it
    // decides are unused after about a week. A home-screen PWA opened three
    // times a week is not obviously "used". This was never requested anywhere,
    // so the durability the write queue advertises has been resting on a
    // default that does not promise it. Fire and forget; Settings reports what
    // the browser actually answered.
    void requestPersistentStorage();

    if (process.env.NODE_ENV !== "production") return;
    // Older browsers / WebViews without service-worker support just
    // skip registration — the app works identically, minus offline.
    if (!("serviceWorker" in navigator)) return;

    navigator.serviceWorker
      // updateViaCache: "none" — always revalidate sw.js itself
      // against the server on update checks, ignoring the HTTP cache
      // (belt-and-braces alongside the Cache-Control: no-cache header
      // set for /sw.js in next.config.ts). Per the Next.js 16 PWA
      // guide (node_modules/next/dist/docs/01-app/02-guides/
      // progressive-web-apps.md).
      .register("/sw.js", { scope: "/", updateViaCache: "none" })
      .catch((err) => {
        // Registration failure is non-fatal: the app simply behaves
        // as if no service worker exists (no offline page, no asset
        // cache). Warn quietly for debugging; never surface to the
        // user or the error-capture pipeline — this is expected in
        // e.g. Firefox private windows.
        console.warn("Service worker registration failed:", err);
      });
  }, []);

  return null;
}
